import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import i18n from '../i18n';
import './DrawBrackets.css';

const API_ROOT = `${process.env.REACT_APP_API_URL || ''}/api/draws`;
// UI texts are resolved from the site's existing seven-language dictionaries.
const lossLimitText = (count, t) => `${count} ${t(Number(count)===1?'draw.loss.one':'draw.loss.few')}`;
const shortLossText = (count,t) => `${count} ${t('draw.loss.short')}`;
const serverErrorKeys = {
  'Создавать и редактировать сетки может только партнёр':'noPermission',
  'Турнир не найден':'notFound',
  'Редактировать может только создатель турнира':'onlyOwner',
  'Добавьте от 2 до 256 участников':'invalidList',
  'Имя участника: от 1 до 120 символов':'invalidNames',
  'Имена участников должны отличаться. Добавьте фамилию или номер однофамильцам.':'duplicateNames',
  'Некорректная жеребьёвка: отсутствует первый участник':'invalidDraw',
  'Ошибка истории: участник не найден':'historyMissing',
  'Ошибка истории: победитель не участвовал':'historyWinner',
  'Название: от 1 до 140 символов':'invalidTitle',
  'Лимит поражений: от 1 до 5':'invalidLossLimit',
  'Выберите систему жеребьёвки':'chooseSystem',
  'Фиксированная сетка пока доступна только для 1 поражения; выберите новую жеребьёвку для 2–5':'fixedOnlyOne',
  'Список участников можно менять только до первой жеребьёвки':'listLocked',
  'Турнир уже был запущен':'alreadyStarted',
  'Недостаточно участников':'notEnough',
  'Введите разные неотрицательные результаты (например, 5 и 3)':'invalidScore',
  'Сначала запустите турнир':'startFirst',
  'Текущий тур не найден':'roundNotFound',
  'Редактировать можно только текущий тур. Для старого результата сначала откатите более поздние туры.':'oldResult',
  'Проход без игры нельзя изменить как результат встречи':'byeResult',
  'Следующий тур сейчас недоступен':'nextUnavailable',
  'Сначала введите результаты всех встреч текущего тура':'finishCurrent',
  'Турнир уже завершён':'alreadyFinished',
  'Первый тур удалить нельзя. Исправляйте его результаты напрямую.':'cannotUndoFirst',
  'Тур не найден':'roundMissing'
};
function localizedServerError(message,status) {
  const key=serverErrorKeys[String(message||'').trim()];
  if (key) return i18n.t(`draw.errors.${key}`);
  // Auth middleware may return English errors; avoid displaying untranslated messages.
  if (status===401) return i18n.t('draw.errors.loginRequired');
  if (status===403) return i18n.t('draw.errors.noPermission');
  if (status===404) return i18n.t('draw.errors.notFound');
  if ((i18n.resolvedLanguage||i18n.language||'ru').split('-')[0]==='ru' && message) return message;
  return i18n.t('draw.errors.withStatus',{status});
}

const getToken = () => localStorage.getItem('token') || '';
async function api(path, opts={}) {
  const token = getToken();
  let response;
  try {response = await fetch(`${API_ROOT}${path}`, {
    ...opts,
    headers: {
      ...(opts.body ? {'Content-Type':'application/json'} : {}),
      ...(token ? {Authorization:`Bearer ${token}`} : {}),
      ...(opts.headers || {})
    }
  });} catch(error) {throw new Error(i18n.t('draw.errors.network'));}
  const body=await response.json().catch(()=>null);
  if (!response.ok) {const error=new Error(localizedServerError(body?.error,response.status));error.status=response.status;throw error;}
  return body;
}
function useDeviceWidth() {
  const [size,setSize]=useState({mobile:false,tablet:false,width:'100%',zoom:1});
  useEffect(()=>{
    const calc=()=>{
      const w=Number(window.screen?.width||0),h=Number(window.screen?.height||0);
      const short=Math.min(w||9999,h||9999);
      const angle=Number(window.screen?.orientation?.angle??window.orientation??0);
      const landscape=Math.abs(angle)%180===90;
      const actual=landscape?Math.max(w,h):short;
      const layout=Number(document.documentElement.clientWidth||window.innerWidth||0);
      const touch=Number(navigator.maxTouchPoints||0)>0 || 'ontouchstart' in window;
      const mobile=touch&&short<=900;
      const compensate=mobile&&actual>0&&layout>actual*1.2;
      const zoom=compensate?Math.round(Math.max(1,Math.min(layout/actual,3.2))*1000)/1000:1;
      const tablet=mobile&&actual>=760;
      setSize(p=>p.mobile===mobile&&p.tablet===tablet&&p.zoom===zoom&&p.width===(compensate?`${Math.round(actual)}px`:'100%')?p:{mobile,tablet,zoom,width:compensate?`${Math.round(actual)}px`:'100%'});
    };
    calc();
    window.addEventListener('resize',calc);
    window.addEventListener('orientationchange',calc);
    return ()=>{window.removeEventListener('resize',calc);window.removeEventListener('orientationchange',calc);};
  },[]);
  return size;
}
function Shell({children}) {
  const size=useDeviceWidth();
  return <main className={`draw-page ${size.mobile?'draw-page--mobile':''} ${size.tablet?'draw-page--wide-touch':''}`} style={{'--draw-width':size.width,'--draw-zoom':size.zoom}}>{children}</main>;
}
function Message({error,onClose}) {
  const {t}=useTranslation();
  return error?<div className="draw-error" role="alert">{error}<button type="button" onClick={onClose} aria-label={t('draw.hide')}>×</button></div>:null;
}
function Status({status}) {const {t}=useTranslation();return <span className={`draw-status draw-status--${status}`}>{t(`draw.status.${status}`,{defaultValue:status})}</span>;}
function TournamentCard({t}) {
  const {t:tr}=useTranslation();
  return <Link className="draw-tournament-card" to={`/draws/${t.id}`}>
    <div className="draw-card-top"><Status status={t.status}/><span>#{t.id}</span></div>
    <strong>{t.title}</strong>
    <div className="draw-meta">{tr(t.system==='fixed'?'draw.fixed':'draw.redraw')} · {tr('draw.eliminateAfter')} {lossLimitText(t.loss_limit,tr)}</div>
    <div className="draw-card-bottom">{t.current_round?tr('draw.round',{value:t.current_round}):tr('draw.awaitStart')}<span>{tr('draw.open')}</span></div>
  </Link>;
}
export function DrawBracketsHome() {
  const {t}=useTranslation();
  const [publicList,setPublicList]=useState([]),[mine,setMine]=useState([]);
  const [partner,setPartner]=useState(false),[error,setError]=useState('');
  const [busy,setBusy]=useState(true),[creating,setCreating]=useState(false);
  const [title,setTitle]=useState(''),[limit,setLimit]=useState(2),[system,setSystem]=useState('redraw');
  const [names,setNames]=useState('');
  const navigate=useNavigate();
  const reload=useCallback(async()=>{
    setBusy(true);
    try {
      const pub=await api('/');
      setPublicList(pub.tournaments||[]);
      if(getToken()) {
        try {const owned=await api('/mine');setPartner(true);setMine(owned.tournaments||[]);}
        catch(e) {setPartner(false);if(!/403|401/.test(e.message))console.warn(e);}
      }
    }catch(e){setError(e.message);}
    finally{setBusy(false);}
  },[]);
  useEffect(()=>{reload();},[reload]);
  async function create(event) {
    event.preventDefault();setError('');setCreating(true);
    try {
      const participants=names.split(/\r?\n/).map(s=>s.trim()).filter(Boolean);
      const r=await api('/',{method:'POST',body:JSON.stringify({title:title.trim(),loss_limit:Number(limit),system,participants})});
      navigate(`/draws/${r.tournament.id}`);
    }catch(e){setError(e.message);}finally{setCreating(false);}
  }
  // Свои турниры уже показаны в разделе «Мои сетки».
  // Для партнёра исключаем их из публичного списка, не изменяя публичный доступ к ним.
  // Посетитель без статуса партнёра продолжает видеть все открытые турниры.
  const myTournamentIds = new Set(mine.map(t => String(t.id)));
  const otherPublicTournaments = partner
    ? publicList.filter(t => !myTournamentIds.has(String(t.id)))
    : publicList;
  return <Shell>
    <div className="draw-head"><div><span className="draw-kicker">BACKGAMMON QUEST · {t('draw.organizer')}</span><h1>{t('draw.title')}</h1><p>{t('draw.intro')}</p></div><span className="draw-head-icon" aria-hidden="true">⌘</span></div>
    <Message error={error} onClose={()=>setError('')}/>
    {partner && <section className="draw-panel"><h2>{t('draw.create')}</h2><form className="draw-create" onSubmit={create}>
      <label>{t('draw.name')}<input required maxLength={140} value={title} onChange={e=>setTitle(e.target.value)} placeholder={t('draw.namePlaceholder')}/></label>
      <div className="draw-create-row">
        <label>{t('draw.eliminateAfter')}<select value={limit} onChange={e=>{const n=Number(e.target.value);setLimit(n);if(n!==1)setSystem('redraw');}}>{[1,2,3,4,5].map(n=><option key={n} value={n}>{lossLimitText(n,t)}</option>)}</select></label>
        <label>{t('draw.system')}<select value={system} onChange={e=>setSystem(e.target.value)}><option value="redraw">{t('draw.redrawOption')}</option><option value="fixed" disabled={limit!==1}>{t('draw.fixedOption')}</option></select></label>
      </div>
      <label>{t('draw.participantsLines')}<textarea required rows={5} value={names} onChange={e=>setNames(e.target.value)} placeholder={t('draw.namesPlaceholder')}/></label>
      <p className="draw-hint">{t('draw.createHint')}</p>
      <button className="draw-btn draw-btn--gold" disabled={creating}>{t(creating?'draw.creating':'draw.createDraft')}</button>
    </form></section>}
    {!partner&&getToken()&&<p className="draw-hint">{t('draw.partnerOnly')}</p>}
    {partner&&<section className="draw-section"><h2>{t('draw.mine')}</h2><div className="draw-cards">{mine.map(t=><TournamentCard t={t} key={t.id}/>)}</div>{!mine.length&&!busy&&<p className="draw-empty">{t('draw.mineEmpty')}</p>}</section>}
    <section className="draw-section"><h2>{t('draw.public')}</h2><div className="draw-cards">{otherPublicTournaments.map(t=><TournamentCard t={t} key={t.id}/>)}</div>{!otherPublicTournaments.length&&!busy&&<p className="draw-empty">{t(partner?'draw.othersEmpty':'draw.publicEmpty')}</p>}</section>
  </Shell>;
}
function Pair({m,players,editable,onSave,busy}) {
  const {t}=useTranslation();
  const a=players.get(Number(m.player1_id)),b=players.get(Number(m.player2_id));
  const [score1,setScore1]=useState(m.score1==null?'':String(m.score1));
  const [score2,setScore2]=useState(m.score2==null?'':String(m.score2));
  useEffect(()=>{setScore1(m.score1==null?'':String(m.score1));setScore2(m.score2==null?'':String(m.score2));},[m.score1,m.score2,m.id]);
  const aWins=m.status==='finished'&&Number(m.winner_id)===Number(a?.id);
  const bWins=m.status==='finished'&&Number(m.winner_id)===Number(b?.id);
  return <article className={`draw-match ${m.bye?'draw-match--bye':''}`}>
    <div className="draw-match-top"><span>{t('draw.match',{value:m.slot})}</span><span>{t(m.bye?'draw.bye':m.status==='finished'?'draw.matchFinished':'draw.pending')}</span></div>
    <div className={`draw-player-row ${aWins?'is-winner':''}`}><span className="draw-player-name" title={a?.name}>{a?.name||'—'} <small>{shortLossText(m.player1_losses_before,t)}</small></span><b>{m.bye?'→':m.score1??'—'}</b></div>
    <div className={`draw-player-row ${bWins?'is-winner':''}`}><span className="draw-player-name" title={b?.name}>{b?.name||t('draw.noOpponent')} {b&&<small>{shortLossText(m.player2_losses_before,t)}</small>}</span><b>{m.bye?'—':m.score2??'—'}</b></div>
    {editable&&!m.bye&&<form className="draw-result-form" onSubmit={e=>{e.preventDefault();onSave(m.id,score1,score2);}}>
      <label><span>{t('draw.result')}</span><div className="draw-score-inputs"><input type="number" min="0" max="9999" inputMode="numeric" required value={score1} onChange={e=>setScore1(e.target.value)} aria-label={t('draw.pointsAria',{name:a?.name||''})}/><span>:</span><input type="number" min="0" max="9999" inputMode="numeric" required value={score2} onChange={e=>setScore2(e.target.value)} aria-label={t('draw.pointsAria',{name:b?.name||''})}/></div></label>
      <button className="draw-btn draw-btn--small" disabled={busy||score1===''||score2===''||Number(score1)===Number(score2)}>{t(m.status==='finished'?'draw.change':'draw.save')}</button>
    </form>}
  </article>;
}
export function DrawBracketsDetail() {
  const {t:tr}=useTranslation();
  const {id}=useParams();const navigate=useNavigate();
  const [data,setData]=useState(null),[mine,setMine]=useState(false);
  const [error,setError]=useState(''),[busy,setBusy]=useState(false);
  const [selected,setSelected]=useState(1),[overview,setOverview]=useState(false);
  const [names,setNames]=useState('');
  const reload=useCallback(async()=>{
    const d=await api(`/${id}`);setData(d);
    setSelected(old=>old>d.tournament.current_round?Math.max(1,d.tournament.current_round):old);
    if(d.tournament.current_round===0)setSelected(1);
    if(d.tournament.status==='draft')setNames(d.participants.map(p=>p.name).join('\n'));
    if(getToken())try {const owned=await api('/mine');setMine((owned.tournaments||[]).some(t=>Number(t.id)===Number(id)));}catch(_){setMine(false);}
  },[id]);
  useEffect(()=>{setData(null);setError('');setSelected(1);reload().catch(e=>setError(e.message));},[reload]);
  async function perform(action,confirmText) {
    if(confirmText&&!window.confirm(confirmText))return;
    setBusy(true);setError('');
    try {await action();await reload();}catch(e){setError(e.message);}finally{setBusy(false);}
  }
  const t=data?.tournament;
  const playerMap=useMemo(()=>new Map((data?.participants||[]).map(p=>[Number(p.id),p])),[data]);
  const rounds=data?.rounds||[],matches=data?.matches||[];
  const chosen=rounds.find(r=>r.number===selected);
  const chosenMatches=chosen?matches.filter(m=>Number(m.round_id)===Number(chosen.id)):[];
  const current=rounds.find(r=>r.number===t?.current_round);
  const currentMatches=current?matches.filter(m=>Number(m.round_id)===Number(current.id)):[];
  const allDone=currentMatches.length>0&&currentMatches.every(m=>m.status==='finished');
  const champion=playerMap.get(Number(t?.champion_id));
  const editable=mine&&selected===t?.current_round;
  const save=(matchId,s1,s2)=>perform(()=>api(`/${id}/matches/${matchId}`,{method:'PUT',body:JSON.stringify({score1:Number(s1),score2:Number(s2)})}));
  const copyShareLink=async()=>{
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(window.location.href);
      window.alert(tr('draw.copied'));
    } catch(_) {window.prompt(tr('draw.copyPrompt'),window.location.href);}
  };
  return <Shell>
    <div className="draw-detail-head"><Link to="/draws" className="draw-back">← {tr('draw.allBrackets')}</Link><button type="button" className="draw-btn draw-btn--small draw-copy" onClick={copyShareLink}>{tr('draw.share')}</button></div>
    <Message error={error} onClose={()=>setError('')}/>
    {!t&&!error&&<p className="draw-empty">{tr('draw.loading')}</p>}
    {t&&<><header className="draw-head"><div><span className="draw-kicker">{tr('draw.bracketNo',{value:t.id})} · <Status status={t.status}/></span><h1>{t.title}</h1><p>{tr('draw.summary',{participants:data.participants.length,losses:lossLimitText(t.loss_limit,tr),system:tr(t.system==='fixed'?'draw.fixedOption':'draw.redrawOption')})}</p></div>{champion&&<div className="draw-champion">🏆 {tr('draw.champion')}<br/><strong>{champion.name}</strong></div>}</header>
      {t.status==='draft'&&mine&&<section className="draw-panel"><h2>{tr('draw.participantsList')}</h2><p className="draw-hint">{tr('draw.editBefore')}</p><textarea rows={Math.min(12,Math.max(5,names.split('\n').length))} value={names} onChange={e=>setNames(e.target.value)} aria-label={tr('draw.participantsAria')}/><div className="draw-actions"><button disabled={busy} className="draw-btn" onClick={()=>perform(()=>api(`/${id}/participants`,{method:'PUT',body:JSON.stringify({participants:names.split(/\r?\n/).filter(s=>s.trim())})}))}>{tr('draw.saveList')}</button><button disabled={busy} className="draw-btn draw-btn--gold" onClick={()=>perform(()=>api(`/${id}/start`,{method:'POST'}),tr('draw.startConfirm'))}>{tr('draw.start')}</button></div></section>}
      {rounds.length>0&&<><section className="draw-panel draw-round-toolbar"><div className="draw-round-title"><h2>{tr('draw.round',{value:selected})}</h2><span>{tr('draw.chooseRound')}</span></div><div className="draw-round-tabs" role="tablist" aria-label={tr('draw.roundsAria')}>{rounds.map(r=><button type="button" key={r.id} role="tab" aria-selected={selected===r.number} className={`draw-round-tab ${selected===r.number?'is-active':''}`} onClick={()=>setSelected(r.number)}>{tr('draw.round',{value:r.number})}<small>{r.status==='done'?'✓':'●'}</small></button>)}</div>
        <div className="draw-round-nav"><button type="button" disabled={selected<=1} onClick={()=>setSelected(n=>n-1)}>{tr('draw.earlier')}</button><span>{selected} / {rounds.length}</span><button type="button" disabled={selected>=rounds.length} onClick={()=>setSelected(n=>n+1)}>{tr('draw.later')}</button></div>
      </section>
      <section className="draw-round-panel"><div className="draw-panel-caption"><h2>{tr('draw.matchesTitle',{value:selected})}</h2><span>{tr('draw.cardsCount',{value:chosenMatches.length})}</span></div><div className="draw-match-grid">{chosenMatches.map(m=><Pair key={m.id} m={m} players={playerMap} editable={editable} busy={busy} onSave={save}/>)}</div>
        {editable&&<div className="draw-actions draw-action-bottom">
          {t.status==='active'&&allDone&&<button className="draw-btn draw-btn--gold" disabled={busy} onClick={()=>perform(async()=>{await api(`/${id}/next-round`,{method:'POST'});setSelected(t.current_round+1);})}>{tr('draw.nextRound')}</button>}
          {t.current_round>1&&<button className="draw-btn draw-btn--warning" disabled={busy} onClick={()=>perform(()=>api(`/${id}/rollback`,{method:'POST'}),tr('draw.rollbackConfirm'))}>{tr('draw.rollback')}</button>}
        </div>}
        {mine&&selected<t.current_round&&<p className="draw-hint">{tr('draw.oldRoundNotice')}</p>}
      </section>
      <section className="draw-section draw-standings">
        <h2>{tr('draw.standings')}</h2>
        <div className="draw-standing-list">
          {data.participants
            .slice()
            .sort((a,b)=>Number(a.eliminated)-Number(b.eliminated)||a.losses-b.losses||a.seed-b.seed)
            .map(p=>{
              // Цвет зависит от ТЕКУЩЕГО числа поражений, а не от результата выбранного тура.
              // После достижения лимита игрок всегда красный, даже если лимит равен 1 или 2.
              const standingColor = p.eliminated
                ? 'draw-standing-row--eliminated'
                : Number(p.losses) === 0
                  ? 'draw-standing-row--zero'
                  : Number(p.losses) === 1
                    ? 'draw-standing-row--one'
                    : 'draw-standing-row--two-plus';
              return <div key={p.id} className={`draw-standing-row ${standingColor}`}>
                <span>{p.name}</span>
                <span>{tr('draw.standingLine',{losses:p.losses,limit:t.loss_limit,byes:p.byes})}</span>
                <span>{p.eliminated?tr('draw.eliminated'):p.id===t.champion_id?`🏆 ${tr('draw.champion')}`:tr('draw.playing')}</span>
              </div>;
            })}
        </div>
      </section>
      {t.system==='fixed'&&rounds.length>1&&<section className="draw-section draw-overview-area"><div className="draw-overview-toggle"><h2>{tr('draw.allBracket')}</h2><button type="button" className="draw-btn draw-btn--small" onClick={()=>setOverview(v=>!v)}>{tr(overview?'draw.hide':'draw.showAll')}</button></div>{overview&&<div className="draw-overview">{rounds.map(r=><div className="draw-overview-col" key={r.id}><h3>{tr('draw.round',{value:r.number})}</h3>{matches.filter(m=>Number(m.round_id)===Number(r.id)).map(m=><Pair key={m.id} m={m} players={playerMap} editable={false} busy={busy}/>)}</div>)}</div>}</section>}
      </>}
      {t.status==='finished'&&champion&&<div className="draw-finish">{tr('draw.finishedLabel')} <strong>{champion.name}</strong></div>}
    </>}
  </Shell>;
}
export default DrawBracketsHome;
